import type { ActionResponseApiResult } from 'api/__GENERATED';
import type { AxiosResponse } from 'axios';
import { timeUtility } from 'utility';
import { errorWrapper } from 'utility/error';

import { apiClient } from './api';
import { getUser } from './OIDC/api';
import { toAction } from './transformers/toAction';

// `suggestionIds` is intentionally omitted from the update body. The server
// rejects any update containing the field when the stored action is in
// Monitor status — even when the value is unchanged — and the UI never
// edits the field anyway (suggestion links are created via the
// createActionFromSuggestion saga). Sending it would either trip that
// validation or silently look like a "change" to the server.
//
// We can't omit a required key through the aliased Zodios method
// (`UpdateActionRequest` is `.strict()` with `suggestionIds` required), so
// we go through the raw axios instance and inject the bearer token
// manually — the Zodios token plugin only runs on aliased calls, not on
// `apiClient.axios` directly.
type UpdateBody = {
  improvementAreaId: string;
  title: string;
  problemStatement: string;
  solutionProposal: string;
  definitionOfDone: string;
  status: Data.Action['status'];
  startAt: string;
  endAt: string;
  priority: Data.Action['priority'];
  responsibleEmployee: string | null;
};

async function updateAction(args: { tenantId: string; action: Data.Action }) {
  const { tenantId, action } = args;

  const body: UpdateBody = {
    improvementAreaId: action.improvementAreaId!,
    title: action.name,
    problemStatement: action.problemStatement,
    solutionProposal: action.solutionProposal,
    definitionOfDone: action.definitionOfDone,
    status: action.status,
    startAt: timeUtility
      .toDate({ type: 'day', ...action.startAt })
      .toISOString(),
    endAt: timeUtility.toDate({ type: 'day', ...action.endAt }).toISOString(),
    priority: action.priority,
    responsibleEmployee: action.responsibleEmployee,
  };

  const token = (await getUser())?.access_token;
  const url = `/v1/tenants/${tenantId}/improvements/actions/${action.id}/update`;
  const apiCall: Promise<AxiosResponse<ActionResponseApiResult>> =
    apiClient.axios.post(url, body, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });

  return await errorWrapper({
    apiCall,
    error: {
      message: '',
      severity: 'warning',
      title: 'Failed to update action',
    },
    onSuccess: (dto) => {
      const updatedAction = toAction(dto);
      return { action: updatedAction };
    },
  });
}

export { updateAction };
